var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { NF_CELL_SIZE } from '../constants/gridConstants';
var CELL_SIZE = NF_CELL_SIZE;
var GRID_PADDING = 0; // No exterior padding around the grid
/**
 * Rotate a point around a center point by a given angle
 * @param point - Point to rotate
 * @param center - Center of rotation
 * @param angleDeg - Angle in degrees
 * @returns Rotated point
 */
export var rotatePointAroundCenter = function (point, center, angleDeg) {
    var angleRad = (angleDeg * Math.PI) / 180;
    var cos = Math.cos(angleRad);
    var sin = Math.sin(angleRad);
    var dx = point.x - center.x;
    var dy = point.y - center.y;
    return {
        x: center.x + dx * cos - dy * sin,
        y: center.y + dx * sin + dy * cos,
    };
};
/**
 * Check if a point is inside a frame's bounds
 * @param point - Point to check
 * @param frame - Frame to check against
 * @returns True if point is inside frame
 */
export var isPointInFrame = function (point, frame) {
    var halfWidth = (frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2;
    var halfHeight = (frame.rows * CELL_SIZE + GRID_PADDING * 2) / 2;
    var minX = frame.center.x - halfWidth;
    var maxX = frame.center.x + halfWidth;
    var minY = frame.center.y - halfHeight;
    var maxY = frame.center.y + halfHeight;
    return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
};
/**
 * Find the nearest cell index to a drop position within a frame
 * @param dropPosition - Position where counter was dropped
 * @param frame - Target frame
 * @returns Cell index (0-based)
 */
export var findNearestCell = function (dropPosition, frame) {
    // Calculate position relative to frame's top-left corner (including padding)
    var halfWidth = (frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2;
    var halfHeight = (frame.rows * CELL_SIZE + GRID_PADDING * 2) / 2;
    var frameLeft = frame.center.x - halfWidth;
    var frameTop = frame.center.y - halfHeight;
    // Subtract padding to get position relative to grid content
    var relativeX = dropPosition.x - frameLeft - GRID_PADDING;
    var relativeY = dropPosition.y - frameTop - GRID_PADDING;
    // Find nearest cell
    var col = Math.max(0, Math.min(frame.cols - 1, Math.round(relativeX / CELL_SIZE)));
    var row = Math.max(0, Math.min(frame.rows - 1, Math.round(relativeY / CELL_SIZE)));
    return row * frame.cols + col;
};
/**
 * Get the position of a cell by its index
 * @param cellIndex - Cell index (0-based)
 * @param frame - Frame containing the cell
 * @returns Absolute position of cell center
 */
export var getCellPosition = function (cellIndex, frame) {
    var row = Math.floor(cellIndex / frame.cols);
    var col = cellIndex % frame.cols;
    var halfWidth = (frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2;
    var halfHeight = (frame.rows * CELL_SIZE + GRID_PADDING * 2) / 2;
    var frameLeft = frame.center.x - halfWidth;
    var frameTop = frame.center.y - halfHeight;
    // Add padding offset to position cells correctly
    return {
        x: frameLeft + GRID_PADDING + col * CELL_SIZE + CELL_SIZE / 2,
        y: frameTop + GRID_PADDING + row * CELL_SIZE + CELL_SIZE / 2,
    };
};
/**
 * Snap counters to grid cells with linear placement
 * @param frame - Target frame
 * @param dropPosition - Position where counter was dropped
 * @param counterCount - Number of counters to place (1, 5, or 10)
 * @param counterConfig - Shape and color configuration
 * @param existingCounters - All existing counters
 * @returns Object with counters placed in frame and overflow counters
 */
export var snapCountersToGrid = function (frame, dropPosition, counterCount, counterConfig, existingCounters) {
    var result = { counters: [], overflowCounters: [] };
    // Find nearest cell to drop position
    var startCellIndex = findNearestCell(dropPosition, frame);
    // Get occupied cells for this frame
    var occupiedCells = new Set(existingCounters
        .filter(function (c) { return c.frameId === frame.id && c.cellIndex !== undefined; })
        .map(function (c) { return c.cellIndex; }));
    var totalCells = frame.rows * frame.cols;
    var currentCellIndex = startCellIndex;
    var placedCount = 0;
    // Try to place counters linearly from the starting cell
    for (var attempt = 0; attempt < counterCount; attempt++) {
        // Find next empty cell starting from current position
        var foundEmptyCell = false;
        for (var i = 0; i < totalCells; i++) {
            var checkIndex = (currentCellIndex + i) % totalCells;
            if (!occupiedCells.has(checkIndex)) {
                // Found empty cell - place counter here
                var position = getCellPosition(checkIndex, frame);
                result.counters.push({
                    id: "numberFrameCounter_".concat(Date.now(), "_").concat(attempt),
                    type: 'numberFrameCounter',
                    shape: counterConfig.shape,
                    color: counterConfig.color,
                    center: position,
                    dimensions: { width: CELL_SIZE, height: CELL_SIZE },
                    scale: 1,
                    rotation: 0,
                    frameId: frame.id,
                    cellIndex: checkIndex,
                    lock: { positioning: false, editing: false },
                });
                occupiedCells.add(checkIndex);
                currentCellIndex = (checkIndex + 1) % totalCells;
                placedCount++;
                foundEmptyCell = true;
                break;
            }
        }
        // No empty cell found - place outside frame
        if (!foundEmptyCell) {
            var overflowIndex = attempt - placedCount;
            var halfWidth = (frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2;
            var frameRight = frame.center.x + halfWidth;
            result.overflowCounters.push({
                id: "numberFrameCounter_".concat(Date.now(), "_").concat(attempt),
                type: 'numberFrameCounter',
                shape: counterConfig.shape,
                color: counterConfig.color,
                center: {
                    x: frameRight + 20 + overflowIndex * (CELL_SIZE + 10),
                    y: frame.center.y,
                },
                dimensions: { width: CELL_SIZE, height: CELL_SIZE },
                scale: 1,
                rotation: 0,
                lock: { positioning: false, editing: false },
            });
        }
    }
    return result;
};
/**
 * Create counters at a specific position (outside any frame)
 * @param position - Position to place counters
 * @param counterCount - Number of counters to place
 * @param counterConfig - Shape and color configuration
 * @returns Array of counter data objects
 */
export var createCountersAtPosition = function (position, counterCount, counterConfig) {
    var counters = [];
    for (var i = 0; i < counterCount; i++) {
        counters.push({
            id: "numberFrameCounter_".concat(Date.now(), "_").concat(i),
            type: 'numberFrameCounter',
            shape: counterConfig.shape,
            color: counterConfig.color,
            center: {
                x: position.x + i * (CELL_SIZE + 10),
                y: position.y,
            },
            dimensions: { width: CELL_SIZE, height: CELL_SIZE },
            scale: 1,
            rotation: 0,
            lock: { positioning: false, editing: false },
        });
    }
    return counters;
};
/**
 * Get all counters associated with a frame
 * @param frameId - Frame ID
 * @param allCounters - All counters in the system
 * @returns Array of counters belonging to the frame
 */
export var getFrameCounters = function (frameId, allCounters) {
    return allCounters.filter(function (counter) { return counter.frameId === frameId; });
};
/**
 * Update counter positions when frame moves
 * @param frameId - Frame that moved
 * @param deltaX - Horizontal movement
 * @param deltaY - Vertical movement
 * @param allCounters - All counters in the system
 * @returns Updated counters array
 */
export var updateCountersOnFrameMove = function (frameId, deltaX, deltaY, allCounters) {
    return allCounters.map(function (counter) {
        if (counter.frameId === frameId) {
            return __assign(__assign({}, counter), { position: {
                    x: counter.center.x + deltaX,
                    y: counter.center.y + deltaY,
                } });
        }
        return counter;
    });
};
/**
 * Update counter positions and rotations when frame rotates
 * @param frame - Frame that rotated
 * @param rotationDelta - Rotation angle change in degrees (usually 90, -90, 180, etc.)
 * @param allCounters - All counters in the system
 * @returns Updated counters array
 */
export var updateCountersOnFrameRotate = function (frame, rotationDelta, allCounters) {
    return allCounters.map(function (counter) {
        var _a;
        if (counter.frameId === frame.id) {
            var rotatedPosition = rotatePointAroundCenter(counter.center, frame.center, rotationDelta);
            return __assign(__assign({}, counter), { position: rotatedPosition, rotation: (((_a = counter.rotation) !== null && _a !== void 0 ? _a : 0) + rotationDelta) % 360 });
        }
        return counter;
    });
};
